import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { EmptyState } from "@/components/app/ui-bits";
import { Plus, PencilSimple, TreeStructure, Prohibit, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const TYPE_LABEL = { asset: "Asset", liability: "Liability", equity: "Equity", revenue: "Revenue", expense: "Expense" };
const TYPE_TONE = {
  asset: "bg-blue-50 text-blue-700 border-blue-200",
  liability: "bg-amber-50 text-amber-700 border-amber-200",
  equity: "bg-violet-50 text-violet-700 border-violet-200",
  revenue: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expense: "bg-rose-50 text-rose-700 border-rose-200",
};

function AccountDialog({ open, onOpenChange, editing, groups, onSave }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm(editing || { code: "", name: "", account_type: "expense", parent_code: "", is_group: false, description: "" });
  }, [editing, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.code?.trim() || !form.name?.trim()) { toast.error("Code and name are required"); return; }
    onSave({ ...form, parent_code: form.parent_code || null });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit account ${editing.code}` : "New GL account"}</DialogTitle>
          <DialogDescription>Accounts drive every journal line. Group headers cannot be posted to.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Code</Label>
            <Input value={form.code || ""} onChange={(e) => set("code", e.target.value)} disabled={!!editing} data-testid="coa-form-code" />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.account_type} onValueChange={(v) => set("account_type", v)} disabled={!!editing}>
              <SelectTrigger data-testid="coa-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Name</Label>
            <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="coa-form-name" />
          </div>
          <div className="col-span-2">
            <Label>Parent (group)</Label>
            <Select value={form.parent_code || "none"} onValueChange={(v) => set("parent_code", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="coa-form-parent"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— No parent —</SelectItem>
                {groups.map((g) => <SelectItem key={g.code} value={g.code}>{g.code} · {g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!editing && (
            <div className="col-span-2 flex items-center gap-2">
              <Checkbox id="is_group" checked={!!form.is_group} onCheckedChange={(v) => set("is_group", !!v)} data-testid="coa-form-isgroup" />
              <Label htmlFor="is_group" className="cursor-pointer">Group header (not postable)</Label>
            </div>
          )}
          <div className="col-span-2">
            <Label>Description</Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-primary-blue" onClick={submit} data-testid="coa-form-save">Save account</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function GLChartOfAccountsTab({ canEdit, isAdmin }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/finance/gl/accounts"); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const items = data?.items || [];
  const groups = useMemo(() => items.filter((a) => a.is_group), [items]);

  const seed = async () => {
    setSeeding(true);
    try {
      const { data: r } = await api.post("/finance/gl/accounts/seed");
      toast.success(`Chart of Accounts seeded · ${r.created} accounts created`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSeeding(false); }
  };

  const save = async (form) => {
    try {
      if (editing) await api.patch(`/finance/gl/accounts/${editing.code}`, { name: form.name, parent_code: form.parent_code, description: form.description });
      else await api.post("/finance/gl/accounts", form);
      toast.success("Account saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (a) => {
    try {
      if (a.active) {
        const { data: r } = await api.delete(`/finance/gl/accounts/${a.code}`);
        toast.success(r.action === "disabled" ? `Account ${a.code} disabled (${r.reason})` : `Account ${a.code} deleted`);
      } else {
        await api.patch(`/finance/gl/accounts/${a.code}`, { active: true });
        toast.success(`Account ${a.code} re-enabled`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="font-mono-alt text-xs uppercase tracking-widest text-slate-400">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="gl-coa-tab">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {Object.entries(TYPE_LABEL).map(([k, l]) => (
          <div key={k} className="dc-card p-3" data-testid={`coa-count-${k}`}>
            <div className="overline">{l}s</div>
            <div className="font-display text-xl font-bold text-slate-900 tabular-nums">{data.counts_by_type?.[k] || 0}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-slate-500">
          Double-entry backbone · every journal line must hit a postable account. Renames never rewrite posted history (lines snapshot the name).
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={seed} disabled={seeding} data-testid="coa-seed-btn">
              <ArrowsClockwise size={12} className={seeding ? "animate-spin mr-1" : "mr-1"} /> Seed standard CoA
            </Button>
          )}
          {canEdit && (
            <Button size="sm" className="btn-primary-blue" onClick={() => { setEditing(null); setOpen(true); }} data-testid="coa-add-btn">
              <Plus size={12} className="mr-1" /> New account
            </Button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={TreeStructure} title="No chart of accounts yet"
          description={isAdmin ? "Seed the standard IFRS-style chart to get started." : "Ask an administrator to seed the Chart of Accounts."} />
      ) : (
        <div className="dc-card responsive-table-wrap">
          <table className="data-table w-full min-w-[900px]">
            <thead>
              <tr>
                <th>Code</th><th>Account</th><th>Type</th><th>Normal balance</th>
                <th>Postable</th><th>Status</th>{canEdit && <th className="w-24">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.code} data-testid={`coa-row-${a.code}`} className={a.is_group ? "bg-slate-50/70" : ""}>
                  <td className="font-mono-alt text-xs tabular-nums">{a.code}</td>
                  <td className={a.is_group ? "font-bold text-slate-900" : "text-slate-800"}>
                    <span style={{ paddingLeft: a.parent_code ? (a.is_group ? 12 : 24) : 0 }}>{a.name}</span>
                  </td>
                  <td><span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${TYPE_TONE[a.account_type] || ""}`}>{TYPE_LABEL[a.account_type] || a.account_type}</span></td>
                  <td className="text-xs uppercase tracking-wider text-slate-600">{a.normal_balance}</td>
                  <td className="text-xs">{a.is_postable ? "Yes" : "Group header"}</td>
                  <td>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider ${a.active ? "text-emerald-600" : "text-rose-500"}`}>
                      {a.active ? "Active" : "Disabled"}
                    </span>
                  </td>
                  {canEdit && (
                    <td>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" className="h-7" onClick={() => { setEditing(a); setOpen(true); }} data-testid={`coa-edit-${a.code}`}>
                          <PencilSimple size={12} />
                        </Button>
                        {!a.system || !a.active ? (
                          <Button variant="outline" size="sm" className="h-7" title={a.active ? "Disable" : "Re-enable"} onClick={() => toggle(a)} data-testid={`coa-toggle-${a.code}`}>
                            <Prohibit size={12} className={a.active ? "" : "text-emerald-600"} />
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" className="h-7" title="Disable system account" onClick={() => toggle(a)} data-testid={`coa-toggle-${a.code}`}>
                            <Prohibit size={12} />
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AccountDialog open={open} onOpenChange={setOpen} editing={editing} groups={groups} onSave={save} />
    </div>
  );
}
